import React from 'react';
import { css, StyleSheet } from 'aphrodite';
import { baseStyleSheet as bs } from '../../styles/styles';
import { white } from '../../styles/colors';

export const DraftCardSkeleton: React.FC = () => {
	return (
		<div className={css(styles.card, bs.shadow)}>
			{/* Title skeleton */}
			<div className={css(styles.titleSkeleton)} />

			{/* Dropdown menu skeleton */}
			<div className={css(styles.menuSkeleton)} />

			{/* Message bubble skeleton */}
			<div className={css(styles.bubbleSkeleton)}>
				<div className={css(styles.textLine)} />
				<div className={css(styles.textLine, styles.textLineShort)} />
			</div>

			{/* Draft tag and date skeleton */}
			<div className={css(bs.flex, bs.itemsCenter, bs.gap2, bs.mb2)}>
				<div className={css(styles.tagSkeleton)} />
				<div className={css(styles.dateSkeleton)} />
			</div>

			{/* Button skeleton */}
			<div className={css(styles.buttonSkeleton)} />
		</div>
	);
};

export const DraftCardSkeletonGrid: React.FC<{ count?: number; 'data-testid'?: string }> = ({
	count = 6,
	'data-testid': dataTestId,
}) => {
	return (
		<div className={css(styles.cardList)} data-testid={dataTestId}>
			{Array.from({ length: count }, (_, index) => (
				<DraftCardSkeleton key={index} />
			))}
		</div>
	);
};

const shimmerAnimation = {
	'0%': { backgroundPosition: '-200px 0' },
	'100%': { backgroundPosition: 'calc(200px + 100%) 0' },
};

const styles = StyleSheet.create({
	cardList: {
		gridTemplateColumns: 'repeat(auto-fill, minmax(min(320px, 100%), 1fr))',
		height: 'calc(100% - 66px)',
		gap: 20,
		display: 'grid',
		padding: 24,
		boxSizing: 'border-box',
		overflow: 'auto',
	},
	card: {
		background: white,
		position: 'relative',
		display: 'flex',
		flexDirection: 'column',
		padding: `${32}px ${24}px`,
		gap: 16,
		alignItems: 'flex-start',
		borderRadius: 8,
		marginBottom: 'auto',
	},
	titleSkeleton: {
		height: 24,
		width: '80%',
		backgroundColor: '#f0f0f0',
		borderRadius: 4,
		background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
		backgroundSize: '200px 100%',
		animationName: shimmerAnimation,
		animationDuration: '2s',
		animationIterationCount: 'infinite',
	},
	menuSkeleton: {
		position: 'absolute',
		right: 12,
		top: 12,
		width: 24,
		height: 24,
		backgroundColor: '#f0f0f0',
		borderRadius: '50%',
		background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
		backgroundSize: '200px 100%',
		animationName: shimmerAnimation,
		animationDuration: '2s',
		animationIterationCount: 'infinite',
	},
	bubbleSkeleton: {
		width: '100%',
		backgroundColor: '#f0f0f0',
		borderRadius: 20,
		padding: 16,
		marginBottom: 8,
		background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
		backgroundSize: '200px 100%',
		animationName: shimmerAnimation,
		animationDuration: '2s',
		animationIterationCount: 'infinite',
	},
	textLine: {
		height: 14,
		backgroundColor: '#e8e8e8',
		borderRadius: 2,
		marginBottom: 8,
		width: '100%',
	},
	textLineShort: {
		width: '60%',
		marginBottom: 0,
	},
	tagSkeleton: {
		height: 20,
		width: 50,
		backgroundColor: '#f0f0f0',
		borderRadius: 4,
		background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
		backgroundSize: '200px 100%',
		animationName: shimmerAnimation,
		animationDuration: '2s',
		animationIterationCount: 'infinite',
	},
	dateSkeleton: {
		height: 16,
		width: 120,
		backgroundColor: '#f0f0f0',
		borderRadius: 4,
		background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
		backgroundSize: '200px 100%',
		animationName: shimmerAnimation,
		animationDuration: '2s',
		animationIterationCount: 'infinite',
	},
	buttonSkeleton: {
		height: 36,
		width: 100,
		backgroundColor: '#f0f0f0',
		borderRadius: 4,
		background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
		backgroundSize: '200px 100%',
		animationName: shimmerAnimation,
		animationDuration: '2s',
		animationIterationCount: 'infinite',
	},
});
